/**
 * 消費一覧・消費詳細の数値（サーバーが返す10進の文字列）を、文字列のまま比べて丸める。
 * 浮動小数点数に変換すると桁が変わり、表示や並べ替えが DB の値と食い違うため、Number() や parseFloat() は使わない。
 */

interface Parts {
  negative: boolean;
  int: string;
  frac: string;
}

function parts(value: string): Parts {
  const negative = value.startsWith('-');
  const [int, frac = ''] = (negative ? value.slice(1) : value).split('.');
  const trimmedInt = int.replace(/^0+(?=\d)/, '');
  const trimmedFrac = frac.replace(/0+$/, '');
  const zero = /^0*$/.test(trimmedInt) && trimmedFrac === '';
  return { negative: negative && !zero, int: trimmedInt || '0', frac: trimmedFrac };
}

function compareAbs(a: Parts, b: Parts): number {
  if (a.int.length !== b.int.length) return a.int.length < b.int.length ? -1 : 1;
  if (a.int !== b.int) return a.int < b.int ? -1 : 1;
  const width = Math.max(a.frac.length, b.frac.length);
  const fa = a.frac.padEnd(width, '0');
  const fb = b.frac.padEnd(width, '0');
  return fa === fb ? 0 : fa < fb ? -1 : 1;
}

/** 2つの10進の文字列を比べる。a が小さければ負、等しければ0、大きければ正 */
export function compareDecimal(a: string, b: string): number {
  const pa = parts(a);
  const pb = parts(b);
  if (pa.negative !== pb.negative) return pa.negative ? -1 : 1;
  const abs = compareAbs(pa, pb);
  return pa.negative ? -abs : abs;
}

/** 数字の並び（整数）に1を足す */
function increment(digits: string): string {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] === '9') {
      out[i] = '0';
    } else {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
  }
  return `1${out.join('')}`;
}

/** 小数 `digits` 桁に四捨五入する。小数部はちょうど `digits` 桁にする */
export function roundHalfUp(value: string, digits: number): string {
  const p = parts(value);
  const frac = p.frac.padEnd(digits + 1, '0');
  let kept = p.int + frac.slice(0, digits);
  if (frac[digits] >= '5') kept = increment(kept);
  const int = kept.slice(0, kept.length - digits) || '0';
  const rounded = digits > 0 ? `${int}.${kept.slice(kept.length - digits)}` : int;
  const isZero = /^[0.]*$/.test(rounded);
  return p.negative && !isZero ? `-${rounded}` : rounded;
}

/** 整数部を3桁ごとにカンマで区切る */
export function groupDigits(value: string): string {
  const negative = value.startsWith('-');
  const [int, frac] = (negative ? value.slice(1) : value).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${frac !== undefined ? `.${frac}` : ''}`;
}

/** 表示の形：小数 `digits` 桁に四捨五入し、整数部を3桁ごとにカンマで区切る */
export function formatDecimal(value: string, digits: number): string {
  return groupDigits(roundHalfUp(value, digits));
}
